import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass, NgIf, NgFor } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { Subscription, UserSettings, Utils } from 'fakturnia-shared';
import { Subject, take, takeUntil } from 'rxjs';
import { settings } from 'environments/environment';
import { AuthService } from 'shared/services/auth.service';
import { SettingsService } from 'shared/services/settings.service';
import { SubscriptionService } from 'shared/services/subscription.service';
import { ConnectedAccountsService } from 'shared/services/connected-accounts.service';
import { ConnectedAccountDto } from 'shared/services/api/connected-accounts.api.service';
import { AddNewAccountComponent } from 'shared/components/add-new-account/add-new-account.component';
import { DateTime } from 'luxon';

@Component({
    selector       : 'user',
    templateUrl    : './user.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'user',
    standalone     : true,
    imports        : [MatButtonModule, MatTooltipModule, MatMenuModule, MatDialogModule, RouterLink, NgIf, NgFor, MatIconModule, NgClass, MatDividerModule],
    styles: [`
        /* Miniaturka logo firmy w menu — adaptacyjna do logo pionowych i poziomych. */
        .mat-mdc-menu-item .company-logo-thumb {
            display: inline-flex !important;
            align-items: center;
            justify-content: center;
            height: 48px !important;
            max-height: 48px !important;
            width: 48px !important;
            min-width: 48px !important;
            max-width: 48px !important;
            padding: 2px;
            box-sizing: border-box;
            flex-shrink: 0;
            overflow: hidden;
        }
        .mat-mdc-menu-item .company-logo-thumb img {
            display: block !important;
            max-height: 44px !important;
            max-width: 44px !important;
            width: auto !important;
            height: auto !important;
            object-fit: contain !important;
            margin: 0 auto !important;
        }
    `]
})
export class UserComponent implements OnInit, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = false;
    user: any = null;
    subscription: Subscription | null = null;
    companyLogoUrl: string | null = null;
    enableSubscriptions = settings.enableSubscriptions;
    connectedAccounts: ConnectedAccountDto[] = [];

    public isAdmin = false
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _authService: AuthService,
        private _settingsService: SettingsService,
        private _subscriptionService: SubscriptionService,
        private _connectedAccountsService: ConnectedAccountsService,
        private _dialog: MatDialog
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to user changes
        this._authService.isLoggedIn$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((isLoggedIn) =>
            {
                if(!isLoggedIn) {
                    this.user = null
                    this.connectedAccounts = []
                    this._connectedAccountsService.clear()
                    return
                }
                this._settingsService.getUserSettings().subscribe({
                    next: (userSettings:UserSettings) => {
                      if(Utils.isDefined(userSettings, "user.role")) this.isAdmin = userSettings.user.role == 'administrator' ? true : false
                      this.user = userSettings.user;
                      this.user.status = 'online'
                      this.subscription = userSettings.subscription || null;
                      this.companyLogoUrl = userSettings.company?.logoUrl || null;
                      // Mark for check
                      this._changeDetectorRef.markForCheck();
                    }
                  })

                // Powiązane konta są ładowane przez AuthService.applyLoginData (z user settings).
                // Subskrypcja na accounts$ poniżej zaktualizuje lokalną kopię automatycznie.
            });

        // Subskrybcja na cache powiązanych kont — auto-update menu po attach/unlink/switch.
        this._connectedAccountsService.accounts$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((accounts) => {
                this.connectedAccounts = accounts;
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void
    {
        // Return if user is not available
        if ( !this.user )
        {
            return;
        }

        // Update the user
        // this._userService.update({
        //     ...this.user,
        //     status,
        // }).subscribe();
    }

    /**
     * Sign out
     */
    signOut(): void
    {
        this._authService.logout({ muted: false})
    }

    // Subskrypcja - helpers
    get subscriptionType(): number {
        return this.subscription?.stage ?? 0;
    }

    get subscriptionDaysRemaining(): number {
        return this._subscriptionService.daysRemaining();
    }

    get isSubscriptionExpiringSoon(): boolean {
        return this.subscriptionDaysRemaining <= 7 && this.subscriptionDaysRemaining > 0;
    }

    get isSubscriptionExpired(): boolean {
        return !this._subscriptionService.isActive();
    }

    getSubscriptionTypeLabel(): string {
        const labels: Record<number, string> = {
            0: 'FREE',
            1: 'START',
            2: 'PRO',
            3: 'BUSINESS'
        };
        return labels[this.subscriptionType] || 'FREE';
    }

    /**
     * Otwiera dialog zarządzania powiązanymi kontami (dodaj / lista).
     */
    openConnectedAccountsDialog(): void {
        this._dialog.open(AddNewAccountComponent, {
            panelClass: ['connected-accounts-dialog', 'add-account-dialog-panel'],
            autoFocus: false,
            maxWidth: '90vw',
            width: '500px'
        });
    }

    /**
     * Przełącza na powiązane konto bezpośrednio z menu (bez otwierania dialogu).
     */
    switchToAccount(account: ConnectedAccountDto): void {
        this._connectedAccountsService.switchTo(account).subscribe();
    }
}
